// Adds the Entra access token (Authorization: Bearer ...) to calls to Feelim's own APIs and
// keeps the session alive while the user works.
//
// - The token goes ONLY to the Feelim Function Apps. Their hosts are read from the same
//   REACT_APP_*_URL variables the app already uses, so there is no second list to keep in sync.
// - It never goes to Blob/SAS URLs, formsubmit, LinkedIn, Wompi, Google or any other host.
// - Fail-open: if a token cannot be obtained the request is sent without it. A backend that
//   enforces answers 401 and the screen shows its usual error.
//
// Session handling, in this order:
//   1. MSAL renews the token silently with the refresh token. Nothing is shown.
//   2. A Feelim API that answers 401 {"error":"unauthorized"} gets ONE retry with a freshly
//      renewed token. Other 401s (for example LinkedIn rejecting its own token) are never retried.
//   3. If MSAL needs the person to sign in again, the registered handler
//      (components/common/SessionExpiredModal) asks for it in a popup. The page is not reloaded,
//      so unsaved work stays where it is, and the request continues by itself once the token is
//      back. If nobody can ask, or the person says "not now", the request goes without a token
//      and we do not ask again for COOLDOWN_MS.
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance } from "../msalInstance";
import { apiScopes } from "../authConfig";
import i18n from "../i18n";

const isMockAuth = process.env.REACT_APP_MOCK_AUTH === "true";

const hostOf = (value) => {
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return null;
  }
};

const FEELIM_HOSTS = new Set(
  [
    process.env.REACT_APP_API_BASE_URL,
    process.env.REACT_APP_GET_POSTS_URL,
    process.env.REACT_APP_SAVE_POST_URL,
    process.env.REACT_APP_UPDATE_POST_URL,
    process.env.REACT_APP_PUBLISH_POST_URL,
    process.env.REACT_APP_LINKEDIN_PROFILE_URL,
    process.env.REACT_APP_THEMES_URL,
    process.env.REACT_APP_LOGOS_URL,
    process.env.REACT_APP_HEATMAP_API_URL,
    process.env.REACT_APP_VIDEO_API_URL,
  ]
    .map(hostOf)
    .filter(Boolean)
);

export const isFeelimApi = (url) => {
  const host = hostOf(url);
  if (!host) return false;
  if (host.endsWith(".blob.core.windows.net")) return false; // SAS URLs never get the token
  return FEELIM_HOSTS.has(host);
};

const TOKEN_TIMEOUT_MS = 8000; // never let a slow token request freeze an API call
const COOLDOWN_MS = 60000; // after "not now" / no way to sign in, stop asking for a minute
let blockedUntil = 0;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const id = setTimeout(() => reject(new Error("token_timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(id);
        resolve(value);
      },
      (err) => {
        clearTimeout(id);
        reject(err);
      }
    );
  });

// ---- "sign in again" hook -------------------------------------------------------------
// The UI registers ONE function that asks the person to sign in again (popup) and resolves
// true when a new token is available. Several requests waiting at once share a single ask.
let reauthHandler = null;
let reauthPromise = null;

export const setReauthHandler = (handler) => {
  reauthHandler = handler;
  return () => {
    if (reauthHandler === handler) reauthHandler = null;
  };
};

const askReauth = () => {
  if (!reauthHandler) return Promise.resolve(false);
  if (!reauthPromise) {
    reauthPromise = (async () => {
      try {
        return !!(await reauthHandler());
      } catch {
        return false;
      }
    })().then((ok) => {
      reauthPromise = null;
      return ok;
    });
  }
  return reauthPromise;
};

const currentAccount = () => msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

const silentToken = async (extra = {}) => {
  const account = currentAccount();
  if (!account) return null;
  const result = await withTimeout(
    msalInstance.acquireTokenSilent({ scopes: apiScopes, account, ...extra }),
    TOKEN_TIMEOUT_MS
  );
  return result?.accessToken || null;
};

// MSAL says the person must sign in again: ask once (popup), then read the new token.
const tokenAfterSignIn = async () => {
  const signedIn = await askReauth();
  if (!signedIn) {
    blockedUntil = Date.now() + COOLDOWN_MS;
    console.warn("API token needs a new sign-in; the person did not complete it.");
    return null;
  }
  try {
    return await silentToken();
  } catch (err) {
    console.warn("API token not available after sign-in:", err?.errorCode || err?.name || "unknown");
    return null;
  }
};

// Returns the access token, or null when there is none (mock auth, signed out, or MSAL
// cannot get one). MSAL caches the token and refreshes it when it expires.
// - { forceRefresh: true } skips the cache (used once after a 401).
// - Temporary errors (network, another MSAL interaction in progress): one retry.
// - "Interaction required" (session expired, consent missing): ask to sign in again.
// MSAL is initialized by MsalProvider; this module does not call initialize().
export const getApiToken = async ({ forceRefresh = false } = {}) => {
  if (isMockAuth) return null;
  if (Date.now() < blockedUntil) return null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await silentToken(forceRefresh && attempt === 0 ? { forceRefresh: true } : {});
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        console.warn("API token needs a new sign-in:", err.errorCode || "interaction_required");
        return tokenAfterSignIn();
      }
      if (attempt === 1) {
        console.warn("API token not available:", err?.errorCode || err?.name || err?.message || "unknown");
        return null;
      }
      await sleep(500);
    }
  }
  return null;
};

// Headers to add to a request to `url`: { Authorization } for Feelim APIs, {} for anything else.
// For axios: headers: { ...yourHeaders, ...(await authHeaders(url)) }
export const authHeaders = async (url) => {
  if (!isFeelimApi(url)) return {};
  const token = await getApiToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const withToken = (init, token) => {
  const headers = new Headers(init.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
};

// Only our own gate answers {"error":"unauthorized"}; it runs before any work is done, so
// repeating the request cannot duplicate anything.
const isGateRejection = async (response) => {
  if (!response || response.status !== 401) return false;
  try {
    const body = await response.clone().json();
    return body?.error === "unauthorized";
  } catch {
    return false;
  }
};

// The body can be sent a second time (strings, FormData, Blob...), a stream cannot.
const canReplay = (input, init) =>
  typeof input === "string" && !(init.body && typeof init.body.getReader === "function");

// The browser's own message for a failed connection ("Failed to fetch", or "Load failed" in
// Safari) is English and technical, so it is replaced with a translated one. Aborts pass
// through untouched, so code that cancels a request still sees AbortError.
const send = async (input, init) => {
  try {
    return await fetch(input, init);
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    console.error("Network error:", err?.message || err);
    throw new Error(i18n.t("errors.network"));
  }
};

// Drop-in replacement for fetch(). Same arguments, same result.
export const apiFetch = async (input, init = {}) => {
  const url = typeof input === "string" ? input : input?.url || String(input);
  if (!isFeelimApi(url)) return fetch(input, init);
  const token = await getApiToken();
  const response = await send(input, token ? withToken(init, token) : init);
  if (!canReplay(input, init) || !(await isGateRejection(response))) return response;
  const fresh = await getApiToken({ forceRefresh: true });
  if (!fresh) return response;
  return send(input, withToken(init, fresh));
};
